import { gql } from '@apollo/client'

export const GET_WORK_ROLES = gql`
    subscription getWorkRoles($params: jsonb!) {
        workRoles: master_workRole {
            id
            label
            metaDescription {
                contentBody
                heading
            }
            isActiveWorkRole(args: { params: $params })
            type
        }
    }
`

export const GET_ORG_METADATA = gql`
    subscription getOrgMetadata($id: String!) {
        orgData: users_user_by_pk(id: $id) {
            organization {
                legalName
                address
                attachments
            }
        }
    }
`

export const GET_ASSETS_STATISTICS = gql`
    subscription getAssetsStatistics {
        users_organization {
            getAssetsCountByCriticalityLabelByOrganization
        }
    }
`

export const GET_ORG_ASSETS = gql`
    subscription getOrgAssets {
        assets: assets_asset(order_by: { created_at: desc_nulls_last }) {
            assetCriticalityLabel
            assetTypeLabel
            id
            label
            tags
            is_monitored
            isAgentActive
            agentDetails
            agentName
            assetMetaDetails
            ipDetails
            description
            operatingSystem
            incidents_aggregate {
                aggregate {
                    count
                }
            }
        }
    }
`

export const GET_ORG_ASSETS_BY_ASSET_TYPE = gql`
    subscription getOrgAssets($assetsWhere: assets_asset_bool_exp) {
        assets: assets_asset(
            where: $assetsWhere
            order_by: { created_at: desc_nulls_last }
        ) {
            assetCriticalityLabel
            assetTypeLabel
            id
            label
            is_monitored
            isAgentActive
            agentDetails
            agentName
            assetMetaDetails
            ipDetails
            description
            operatingSystem
            tags
            incidents_aggregate {
                aggregate {
                    count
                }
            }
            totalResolvedLinkedIncident: incidents_aggregate(
                where: { incident: { isResolved: { _eq: true } } }
            ) {
                aggregate {
                    count
                }
            }
            totalAlerts: alerts_aggregate {
                aggregate {
                    count
                }
            }
            totalEvents: alertEvents_aggregate {
                aggregate {
                    count
                }
            }
        }
    }
`

export const GET_INCIDENT_LIST_OVERVIEW = gql`
    subscription GET_INCIDENT_LIST_OVERVIEW {
        incidents: incidents_incident(
            order_by: { created_at: desc_nulls_first }
        ) {
            type
            id
            assets {
                assetId
                asset {
                    label
                    assetCriticalityLabel
                }
            }
            detectedBy
            cirtLeaderUser {
                id
                firstName
                lastName
                attachments
            }
            displayId
            displayName
            activeTaskBoardDetails
            occuredOn
            occuredOnYear
            isResolved
            incidentSeverity
            occuredOnQuarter
        }
    }
`

export const GET_INCIDENT_DETAILS = gql`
    subscription getIncidentDetails(
        $order_by: [tasks_task_order_by!]!
        $incidentId: String!
        $where: tasks_task_bool_exp = {}
    ) {
        incidents: incidents_incident_by_pk(id: $incidentId) {
            incidentTypeId
            activeTaskBoardDetails
            assets {
                asset {
                    id
                    label
                    assetCriticalityLabel
                }
            }
            alert_incidents_aggregate {
                aggregate {
                    count
                }
            }
            incidentLocation
            notes
            detectedBy
            occuredOn
            incidentSeverity
            type
            updated_at
            displayId
            displayName
            updatedByUser {
                firstName
                lastName
            }
            resolvedByUser {
                firstName
                lastName
            }
            isResolved
            resolvedAt
            stages: taskBoards(order_by: { position: desc_nulls_last }) {
                stageIncidentId: id
                stageName: label
                metaDescriptionId
                stageIconUrl
                description
                taskBoardStatuses {
                    status
                    bgColor
                    textColor
                    tasksByStatus(order_by: $order_by, where: $where) {
                        taskId: id
                        displayId
                        assignedTo
                        label
                        status
                        preferredWorkRoles
                        updated_at
                        subTasks {
                            id
                            isCompleted
                        }
                        taskAssignedTo {
                            id
                            firstName
                            attachments
                        }
                    }
                }
            }
        }
    }
`

// Keep in sync with GET_INCIDENT_DETAILS (alerts fields removed for production)
export const GET_INCIDENT_DETAILS_WITHOUT_ALERTS = gql`
    subscription getIncidentDetailsWithoutAlerts(
        $order_by: [tasks_task_order_by!]!
        $incidentId: String!
        $where: tasks_task_bool_exp = {}
    ) {
        incidents: incidents_incident_by_pk(id: $incidentId) {
            incidentTypeId
            activeTaskBoardDetails
            assets {
                asset {
                    id
                    label
                    assetCriticalityLabel
                }
            }
            incidentLocation
            notes
            detectedBy
            occuredOn
            incidentSeverity
            type
            updated_at
            displayId
            displayName
            updatedByUser {
                firstName
                lastName
            }
            resolvedByUser {
                firstName
                lastName
            }
            isResolved
            resolvedAt
            stages: taskBoards(order_by: { position: desc_nulls_last }) {
                stageIncidentId: id
                stageName: label
                metaDescriptionId
                stageIconUrl
                description
                taskBoardStatuses {
                    status
                    bgColor
                    textColor
                    tasksByStatus(order_by: $order_by, where: $where) {
                        taskId: id
                        displayId
                        assignedTo
                        label
                        status
                        preferredWorkRoles
                        updated_at
                        subTasks {
                            id
                            isCompleted
                        }
                        taskAssignedTo {
                            id
                            firstName
                            attachments
                        }
                    }
                }
            }
        }
    }
`

export const GET_SIMULATED_INCIDENT_DETAILS = gql`
    subscription GET_SIMULATED_INCIDENT_DETAILS(
        $order_by: [tasks_task_order_by!]!
        $incidentId: String!
        $where: tasks_task_bool_exp = {}
    ) {
        incidents: incidents_incident_by_pk(id: $incidentId) {
            simulationParam {
                parameters
            }
            incidentTypeId
            activeTaskBoardDetails
            assets {
                asset {
                    id
                    label
                    assetCriticalityLabel
                }
            }
            alert_incidents_aggregate {
                aggregate {
                    count
                }
            }
            incidentLocation
            notes
            detectedBy
            occuredOn
            incidentSeverity
            type
            updated_at
            displayId
            displayName
            updatedByUser {
                firstName
                lastName
            }
            resolvedByUser {
                firstName
                lastName
            }
            isResolved
            resolvedAt
            stages: taskBoards(order_by: { position: desc_nulls_last }) {
                stageIncidentId: id
                stageName: label
                metaDescriptionId
                stageIconUrl
                description
                taskBoardStatuses {
                    status
                    bgColor
                    textColor
                    tasksByStatus(order_by: $order_by, where: $where) {
                        taskId: id
                        displayId
                        assignedTo
                        label
                        status
                        preferredWorkRoles
                        updated_at
                        subTasks {
                            id
                            isCompleted
                        }
                        taskAssignedTo {
                            id
                            firstName
                            attachments
                        }
                    }
                }
            }
        }
    }
`

// Keep in sync with GET_SIMULATED_INCIDENT_DETAILS (alerts fields removed for production)
export const GET_SIMULATED_INCIDENT_DETAILS_WITHOUT_ALERTS = gql`
    subscription GET_SIMULATED_INCIDENT_DETAILS_WITHOUT_ALERTS(
        $order_by: [tasks_task_order_by!]!
        $incidentId: String!
        $where: tasks_task_bool_exp = {}
    ) {
        incidents: incidents_incident_by_pk(id: $incidentId) {
            simulationParam {
                parameters
            }
            incidentTypeId
            activeTaskBoardDetails
            assets {
                asset {
                    id
                    label
                    assetCriticalityLabel
                }
            }
            incidentLocation
            notes
            detectedBy
            occuredOn
            incidentSeverity
            type
            updated_at
            displayId
            displayName
            updatedByUser {
                firstName
                lastName
            }
            resolvedByUser {
                firstName
                lastName
            }
            isResolved
            resolvedAt
            stages: taskBoards(order_by: { position: desc_nulls_last }) {
                stageIncidentId: id
                stageName: label
                metaDescriptionId
                stageIconUrl
                description
                taskBoardStatuses {
                    status
                    bgColor
                    textColor
                    tasksByStatus(order_by: $order_by, where: $where) {
                        taskId: id
                        displayId
                        assignedTo
                        label
                        status
                        preferredWorkRoles
                        updated_at
                        subTasks {
                            id
                            isCompleted
                        }
                        taskAssignedTo {
                            id
                            firstName
                            attachments
                        }
                    }
                }
            }
        }
    }
`

export const GET_INCIDENT_DETAILS_FOR_EDIT = gql`
    subscription GET_INCIDENT_DETAILS_FOR_EDIT($incidentId: String!) {
        incidents: incidents_incident_by_pk(id: $incidentId) {
            displayName
            assets {
                incidentAssetsId: id
                asset {
                    id
                    label
                    assetCriticalityLabel
                }
            }
            isResolved
            incidentLocation
            notes
            detectedBy
            occuredOn
            incidentSeverity
            incidentImpact
            incidentUrgency
        }
    }
`

export const GET_INCIDENT_LIST = gql`
    subscription GET_INCIDENT_LIST($where: incidents_incident_bool_exp = {}) {
        incidents: incidents_incident(where: $where) {
            type
            id
            assets {
                assetId
                asset {
                    label
                    assetCriticalityLabel
                    tags
                }
            }
            detectedBy
            cirtLeaderUser {
                id
                firstName
                lastName
                attachments
            }
            isResolved
            displayId
            displayName
            occuredOn
            incidentSeverity
            activeTaskBoardDetails
        }
    }
`

export const GET_INCIDENT_LIST_FOR_REPORTS = gql`
    subscription GET_INCIDENT_LIST_FOR_REPORTS(
        $where: incidents_incident_bool_exp = {}
    ) {
        incidents: incidents_incident(where: $where) {
            type
            id
            assets {
                assetId
                asset {
                    label
                    assetCriticalityLabel
                }
            }
            detectedBy
            cirtLeaderUser {
                firstName
                lastName
                attachments
                id
            }
            displayId
            reportPassword
            isResolved
            occuredOn
            occuredOnYear
            incidentSeverity
            activeTaskBoardDetails
        }
    }
`

export const GET_USER_ORG_DETAILS = gql`
    subscription getUserOrgDetails {
        users_organization {
            industrySector
            size
            legalName
            address
            attachments
            timezone
        }
    }
`

export const GET_TASK_DETAILS = gql`
    subscription GET_TASK_DETAILS($id: String!) {
        task: tasks_task_by_pk(id: $id) {
            created_at
            updated_at
            label
            taskBoard {
                stageIconUrl
                label
            }
            taskType
            preferredWorkRoles
            complexity
            content
            outcomes {
                label
            }
            notes
            displayId
            subTasks(order_by: { id: desc }) {
                content
                subTaskType
                position
                isCompleted
                id
            }
            incident {
                id
                isResolved
            }
            updatedByUser {
                firstName
                lastName
            }
            taskBoardStatus {
                status
                textColor
            }
            taskArtifacts {
                id
                s3Key
                attachmentUrl
            }
            taskAssignedTo {
                firstName
                lastName
                attachments
                email
                attachments
                id
            }
        }
    }
`

export const GET_USERS = gql`
    subscription getUsers {
        users: users_user {
            id
            email
            firstName
            lastName
            phoneNumber
            hasAcceptedInvite
            resendInviteAttempt
            isAdministrator
            attachments
            workRoles {
                workRole {
                    label
                }
            }
        }
    }
`

export const GET_USERS_WITH_FILTER = gql`
    subscription getUsersWithFilter($where: users_user_bool_exp = {}) {
        users: users_user(where: $where) {
            id
            email
            firstName
            lastName
            phoneNumber
            hasAcceptedInvite
            resendInviteAttempt
            isAdministrator
            attachments
            isActive
            invitationExpiresAt
            workRoles {
                workRole {
                    label
                }
            }
        }
    }
`

export const GET_USERS_FOR_TASKS = gql`
    subscription getUserForTasks {
        users: users_user(where: { isActive: { _eq: true } }) {
            email
            id
            attachments
            firstName
            lastName
            workRoles {
                workRole {
                    label
                }
            }
            assignedTasks_aggregate(
                where: {
                    status: { _neq: "Done" }
                    incidentView: { activeStage: { _neq: "Lessons Learned" } }
                }
            ) {
                aggregate {
                    count
                }
            }
        }
    }
`

export const GET_USER_DETAILS_FOR_CONTEXT = gql`
    subscription GET_USER_DETAILS_FOR_CONTEXT(
        $email: String!
        $organizationId: String!
    ) {
        users: users_user(
            where: {
                email: { _eq: $email }
                organizationId: { _eq: $organizationId }
            }
        ) {
            email
            firstName
            lastName
            phoneNumber
            attachments
            isAdministrator
            auth0UserId
            id
            created_at
            organizationId
            lastSeenNotificationId
            organization {
                id
                timezone
                legalName
                created_at
                attachments
                isRevoked
            }
            workRoles {
                workRole {
                    permissions
                    organization_workroles {
                        permissions
                    }
                }
            }
        }
    }
`

export const GET_USER_DETAILS = gql`
    subscription getUserDetails($id: String! = "") {
        user: users_user_by_pk(id: $id) {
            id
            email
            additionalEmail
            emailNotifications
            commentReplyNotifications
            commentMentionNotifications
            firstName
            isAdministrator
            lastName
            phoneNotifications
            phoneNumber
            totalAvailabilityHours
            title
            attachments
            unassignTasksAttempt
            isActive
            workRoles {
                workRoleId
                workRole {
                    label
                }
            }
        }
    }
`

export const GET_NOTIFICATION_LOGS = gql`
    subscription GET_NOTIFICATION_LOGS(
        $userId: String! = ""
        $organizationId: String! = ""
    ) {
        notifications: notifications_notificationLogs(
            limit: 10
            order_by: { created_at: desc_nulls_last }
            where: {
                _or: [
                    { isPublic: { _eq: true } }
                    { organizationId: { _eq: $organizationId } }
                    { userId: { _eq: $userId } }
                ]
            }
        ) {
            isPublic
            id
            displayContent
            created_at
        }
    }
`

export const GET_TASKS_LIST_TASK_REEL = gql`
    subscription getTasksListTaskReel(
        $assignedTo: String! = ""
        $status: [String!] = ""
    ) {
        tasks: tasks_task(
            where: {
                _and: [
                    { assignedTo: { _eq: $assignedTo } }
                    { incident: { isResolved: { _eq: false } } }
                    { status: { _in: $status } }
                ]
            }
        ) {
            taskId: id
            label
            status
            taskType
            assignedTo
            assignedBy
            updated_at
            displayId
            taskBoard {
                label
                stageIconUrl
            }
            incidentId
            taskAssignedTo {
                id
                firstName
                lastName
                attachments
            }
        }
    }
`

export const GET_INCIDENT_TYPES_BY_ORG = gql`
    subscription getIncidentTypeTemplate {
        incidentTypes: incidents_incidentType(
            where: { isArchived: { _eq: false } }
            order_by: [{ label: asc }]
        ) {
            label
            id
            organizationId
            status
            metaDescription {
                contentBody
                assets
            }
        }
    }
`

export const APPCUES_FLOW = gql`
    subscription APPCUES_FLOW {
        helpCenter_appcuesFlow_aggregate(
            order_by: { priority: asc_nulls_last }
        ) {
            nodes {
                id
                label
                appcuesId
                permalink
            }
        }
    }
`

export const GET_RISK_MANAGEMENT_LOGS = gql`
    subscription getRiskManagementLogs {
        logs: riskManagement_riskManagementLog(
            where: { isFrozen: { _eq: false } }
            order_by: { created_at: desc_nulls_first }
            limit: 1
        ) {
            id
            scoreDetail
            rmLogFunctions {
                id
                identifier
                label
                scoreDetail
                description
                categories {
                    identifier
                    label
                    id
                    scoreDetail
                    maturityRating
                    status
                    function {
                        label
                    }
                }
            }
        }
    }
`

export const GET_RM_LOG_CATEGORIES_STATS = gql`
    subscription getCategoryFromLogId($_eq: String! = "") {
        categories: riskManagement_rmLogCategory(
            where: { function: { rmLogId: { _eq: $_eq } } }
        ) {
            label
            scoreDetail
            function {
                label
            }
        }
    }
`
export const GET_ALERT_THRESHOLD_SETTINGS = gql`
    subscription orgAlertThreshold {
        orgAlertThreshold: alerts_alertOrgSetting(
            where: { alertHandleStatus: { _eq: "ESCALATE" } }
        ) {
            alertCriticalityLevel
            alertHandleStatus
        }
    }
`
export const GET_ALERT_DETAILS_ONE = gql`
    subscription getAlertDetails($id: String!) {
        alerts_alert_by_pk(id: $id) {
            id
            agent_ip
            agent_name
            alertTimestamp
            alert_log
            alert_tactic
            alert_technique
            asset {
                id
                label
                assetCriticality {
                    color
                    label
                }
            }
            alertCriticality {
                color
            }
            alert_level
            alert_criticality_label
            alert_source
            alertDescription
            potentialIncidentSeverity
            potentialIncidentImpact
            potentialIncidentUrgency
            potentialIncidentType
            potentialIncidentTypeId
            alertSummary
            dest_ip
            dest_port
            signature
            src_ip
            src_port
            alertCategory
            ioc_dict
            incidentType {
                metaDescription {
                    contentBody
                    assets
                }
            }
            alert_incidents {
                incidentId
                alertId
                incident {
                    displayId
                }
            }
        }
    }
`

// TODO ts - check this
export const GET_ALL_ALERT_TABLE = gql`
    subscription alertTable($where: alerts_alert_bool_exp!) {
        alerts_alert_aggregate(where: $where) {
            aggregate {
                count
            }
            nodes {
                id
                agent_ip
                agent_name
                alertTimestamp
                alert_log
                alert_tactic
                alert_technique
                asset {
                    id
                    label
                    assetCriticality {
                        color
                        label
                        assets {
                            id
                        }
                    }
                }
                alertCriticality {
                    color
                }
                alert_level
                alert_criticality_label
                alert_source
                alertDescription
                potentialIncidentSeverity
                potentialIncidentType
                alertSummary
                dest_ip
                dest_port
                signature
                src_ip
                src_port
                alertCategory
            }
        }
    }
`
export const GET_PAYMENT = {
    LIST: gql`
        subscription getInvoiceList {
            payments_invoice(
                order_by: { createdAt: desc }
                where: {
                    _not: {
                        status: { _neq: "SUCCEEDED" }
                        comment: { _eq: "UPGRADE" }
                    }
                }
            ) {
                id
                attempt
                billingEmail
                currency
                status
                createdAt
                stripeCustomerId
                stripePaymentMethodId
                updatedAt
                invoiceNumber
                comment
                invoiceDetails
            }
        }
    `,
}
export const GET_INVOICE = gql`
    subscription invoice($id: String!) {
        payments_invoice_by_pk(id: $id) {
            id
            status
            invoice_with_email
            invoiceDetails
            creditUsed
            organizationId
        }
    }
`

export const GET_TEMPLATES = gql`
    subscription GET_TEMPLATES {
        templates: templates_template(order_by: { createdAt: asc }) {
            id
            organizationId
            template
            updateByUser {
                firstName
                lastName
            }
            createdAt
            updatedAt
            organization {
                timezone
            }
            status
        }
    }
`

export const GET_SAMPLE_DATA = gql`
    subscription GET_SAMPLE_DATA {
        organizations: users_organization {
            hasSampleData
            legalName
        }
    }
`

export const GET_WORKROLES = gql`
    subscription GET_WORKROLES($where: master_workRole_bool_exp) {
        workRoles: master_workRole(where: $where) {
            id
            label
            type
            metaDescription {
                id
                description: contentBody
            }
            organizationId
            permissions
            organization_workroles {
                permissions
            }
        }
    }
`

export const GET_ALERT_EVENT_DETAIL_ONE = gql`
    subscription getAlertDetails($id: String!) {
        alerts_alertEvent_by_pk(id: $id) {
            id
            agent_ip
            agent_name
            alertTimestamp
            alert_log
            alert_tactic
            alert_technique
            asset {
                id
                label
                assetCriticality {
                    color
                    label
                }
            }
            alertCriticality {
                color
            }
            alert_level
            alert_criticality_label
            alert_source
            alertDescription
            potentialIncidentSeverity
            potentialIncidentImpact
            potentialIncidentUrgency
            potentialIncidentType
            potentialIncidentTypeId
            alertSummary
            dest_ip
            dest_port
            signature
            src_ip
            src_port
            alertCategory
            ioc_dict
            incidentType {
                metaDescription {
                    contentBody
                    assets
                }
            }
            # alert_incidents {
            #     incidentId
            #     alertId
            #     incident {
            #         displayId
            #     }
            # }
        }
    }
`
export const GET_AGENT_GUIDE_ASSET = gql`
    subscription GET_AGENT_GUIDE_ASSET($assetId: String!) {
        agentGuide: assets_asset_by_pk(id: $assetId) {
            id
            label
            is_monitored
            isAgentActive
            agentName
            agentDetails
            organization {
                id
            }
        }
    }
`

export const GET_SIMULATION_ACTIVE_USERS = gql`
    subscription GET_SIMULATION_ACTIVE_USERS {
        activeUsers: simulation_activeUsers {
            userId
            lastPing
            user {
                attachments
                email
                firstName
                lastName
            }
        }
    }
`

export const TASK_ACTION_RESPONSES = gql`
    subscription TASK_ACTION_RESPONSES($subTaskId: String!) {
        actionResponses: simulation_taskActionResponse(
            where: { subTaskId: { _eq: $subTaskId } }
        ) {
            id
            answeredKey
            content
            created_at
            reasoning
            reasoning_incorrect
        }
    }
`

export const GET_TASKACTION_EVALUATION_STATUS = gql`
    subscription GET_TASKACTION_EVALUATION_STATUS($taskId: String!) {
        subTasks: tasks_subTask(where: { taskId: { _eq: $taskId } }) {
            id
            taskActionResponse {
                correct
                answered
            }
        }
    }
`

export const GET_SIMULATION_INJECT_READINESS = gql`
    subscription GET_SIMULATION_INJECT_READINESS($incidentId: String!) {
        incident: incidents_incident_by_pk(id: $incidentId) {
            id
            incidentTypeId
        }
        injects: injects_inject_aggregate(
            where: { incidentId: { _eq: $incidentId } }
        ) {
            aggregate {
                count
            }
        }
    }
`

export const GET_SIMULATION_INJECTS = gql`
    subscription GET_SIMULATION_INJECTS($simulationId: String!) {
        injects: simulation_incident_inject(
            where: { incidentId: { _eq: $simulationId } }
            order_by: { created_at: desc }
        ) {
            created_at
            inject {
                id
                impact
                urgency
                title
                description
                category {
                    name
                    imageUrl
                }
            }
        }
    }
`

export const GET_PLAYBOOKS = gql`
    subscription GET_PLAYBOOKS {
        playbooks: incidents_incidentType(
            where: { isArchived: { _eq: false } }
            order_by: { label: asc }
        ) {
            label
            id
            organizationId
            updated_at
            status
            updatedByUser {
                id
                firstName
                lastName
                attachments
            }
            metaDescription {
                contentBody
                assets
            }
        }
    }
`

export const COMMS_SCORE = gql`
    subscription COMMS_SCORE {
        templates: templates_template {
            id
            status
        }
    }
`

export const RESPONSE_READINESS_SCORE = gql`
    subscription RESPONSE_READINESS_SCORE {
        organizations: users_organization {
            getResponseReadinessScore
        }
    }
`

export const GET_TOTAL_WORKROLES = gql`
    subscription GET_TOTAL_WORKROLES {
        workRoles: master_workRole_aggregate {
            aggregate {
                count
            }
        }
    }
`

export const GET_TOTAL_ASSIGNED_WORKROLES = gql`
    subscription GET_TOTAL_ASSIGNED_WORKROLES {
        organizations: users_organization {
            getAssignedWorkRolesCount
        }
    }
`

export const ORGANIZATION_ACTIONS = gql`
    subscription GET_ORGANIZATION_RECOMMENDED_ACTIONS {
        actions: users_organization_recommmended_action(
            order_by: { order_id: asc }
        ) {
            id
            title
            description
            done
        }
    }
`

export const GLEAP_PRODUCT_TOURS = gql`
    subscription GLEAP_PRODUCT_TOURS {
        tours: helpCenter_gleap_product_tours(order_by: { order_id: asc }) {
            tour_id
            tour_name
            tour_start_page
        }
    }
`
export const BILLING_DATA = gql`
    subscription BILLING_DATA {
        rows: subscription_active_module_subscriptions {
            organization_id
            module_name
            is_trial
            source
            subscription_start
            subscription_end
            module_start
            module_end
            cancelled_at
            subscription_id
            remaining_days
            total_days
            billing_method
            billing_frequency
            will_renew
            module_cancelled_at
            status
        }
    }
`

export const USER_DETAILS_FOR_CONTEXT = gql`
    subscription USER_DETAILS_FOR_CONTEXT($email: String!) {
        users: users_user(where: { email: { _eq: $email } }) {
            email
            firstName
            lastName
            phoneNumber
            attachments
            isAdministrator
            auth0UserId
            id
            created_at
            organizationId
            lastSeenNotificationId
            organization {
                id
                creditAmount
                timezone
                legalName
                created_at
                attachments
                isRevoked
                billingEmail
                billingAddress
                billing_method
                billing_frequency
            }
            workRoles {
                workRoleLabel
                workRoleId
                workRole {
                    permissions
                    organization_workroles {
                        permissions
                    }
                }
            }
        }
    }
`

export const GET_FEATURE_FLAGS = gql`
    subscription GET_FEATURE_FLAGS {
        features: master_featureFlags(where: { enabled: { _eq: true } }) {
            feature
            enabled
        }
    }
`

export const GET_CHAT_COMMENTS = gql`
    subscription GET_CHAT_COMMENTS(
        $commentCategory: jsonb
        $orderDirection: order_by! = desc
    ) {
        comments: comments_comment(
            order_by: { created_at: $orderDirection }
            where: { commentCategory: { _contains: $commentCategory } }
        ) {
            id
            parentComment {
                id
                editorState
                html
                created_at
                updated_at
                commentedByUser {
                    id
                    firstName
                    lastName
                    attachments
                }
            }
            editorState
            html
            created_at
            updated_at
            commentCategory
            commentedByUser {
                id
                firstName
                lastName
                attachments
            }
        }
    }
`

export const LAST_SEEN_COMMENT = gql`
    subscription LAST_SEEN_COMMENT($incidentId: String!) {
        comments: incidents_incident_chat_last_seen_message(
            where: { incident_id: { _eq: $incidentId } }
            limit: 1
        ) {
            comment {
                id
                created_at
            }
        }
    }
`
